import React from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, SearchIcon, BookOpenIcon, UsersIcon, CalendarIcon, BriefcaseIcon } from 'lucide-react';
const Scholarships: React.FC = () => {
  const navigate = useNavigate();
  // Sample scholarship data
  const scholarships = [{
    id: 'SCH001',
    title: 'Future Leaders Engineering Scholarship',
    program: 'Bachelor of Science in Engineering',
    seats: 5,
    status: 'Active',
    applications: 12,
    createdAt: '2023-08-15'
  }, {
    id: 'SCH002',
    title: 'Digital Innovation MBA Scholarship',
    program: 'Master of Business Administration',
    seats: 3,
    status: 'Pending MoHESR',
    applications: 0,
    createdAt: '2023-09-02'
  }, {
    id: 'SCH003',
    title: 'Healthcare Excellence Scholarship',
    program: 'Doctor of Medicine',
    seats: 2,
    status: 'Draft',
    applications: 0,
    createdAt: '2023-09-10'
  }, {
    id: 'SCH004',
    title: 'Computer Science Innovation Grant',
    program: 'Master of Science in Computer Science',
    seats: 4,
    status: 'Closed',
    applications: 28,
    createdAt: '2023-05-20'
  }];
  // Status badge color mapping
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-blue-100 text-blue-800';
      case 'Pending MoHESR':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <Layout title="My Scholarships" persona="Company/Partner" data-id="element-2447">
      <div className="max-w-6xl mx-auto" data-id="element-2448">
        {/* Header with actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4" data-id="element-2449">
          <div data-id="element-2450">
            <h1 className="text-2xl font-semibold text-gray-800" data-id="element-2451">
              Partnership Scholarships
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-2452">
              Manage your scholarship programs and track applications
            </p>
          </div>
          <button onClick={() => navigate('/employer/create-scholarship')} className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors flex items-center self-start" data-id="element-2453">
            <PlusIcon className="w-4 h-4 mr-2" data-id="element-2454" />
            Create New Scholarship
          </button>
        </div>

        {/* Filters and search */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6" data-id="element-2455">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-2456">
            <div className="relative flex-grow" data-id="element-2457">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2458">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2459" />
              </div>
              <input type="text" placeholder="Search scholarships..." className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-2460" />
            </div>
            <div className="flex gap-2" data-id="element-2461">
              <select className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-2462">
                <option value="" data-id="element-2463">All Statuses</option>
                <option value="active" data-id="element-2464">Active</option>
                <option value="pending" data-id="element-2465">Pending MoHESR</option>
                <option value="draft" data-id="element-2466">Draft</option>
                <option value="closed" data-id="element-2467">Closed</option>
              </select>
              <button className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center" data-id="element-2468">
                <FilterIcon className="w-4 h-4 mr-2" data-id="element-2469" />
                Filter
              </button>
            </div>
          </div>
        </div>

        {/* Scholarships list */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2470">
          <div className="overflow-x-auto" data-id="element-2471">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2472">
              <thead className="bg-gray-50" data-id="element-2473">
                <tr data-id="element-2474">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2475">
                    Scholarship
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2476">
                    Program
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2477">
                    Seats
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2478">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2479">
                    Applications
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2480">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2481">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2482">
                {scholarships.map(scholarship => <tr key={scholarship.id} className="hover:bg-gray-50" data-id="element-2483">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2484">
                      <div className="flex items-center" data-id="element-2485">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center" data-id="element-2486">
                          <BookOpenIcon className="h-5 w-5 text-primary-600" data-id="element-2487" />
                        </div>
                        <div className="ml-4" data-id="element-2488">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2489">
                            {scholarship.title}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2490">
                            {scholarship.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2491">
                      <div className="text-sm text-gray-900" data-id="element-2492">
                        {scholarship.program}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2493">
                      <div className="flex items-center" data-id="element-2494">
                        <UsersIcon className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-2495" />
                        <span className="text-sm text-gray-900" data-id="element-2496">
                          {scholarship.seats}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2497">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusBadgeClass(scholarship.status)}`} data-id="element-2498">
                        {scholarship.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2499">
                      {scholarship.applications}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2500">
                      <div className="flex items-center text-sm text-gray-500" data-id="element-2501">
                        <CalendarIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-2502" />
                        {scholarship.createdAt}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2503">
                      <button onClick={() => navigate(`/employer/scholarships/${scholarship.id}`)} className="text-primary-600 hover:text-primary-900 mr-3" data-id="element-2504">
                        View
                      </button>
                      {scholarship.status === 'Draft' && <button className="text-gray-600 hover:text-gray-900" data-id="element-2505">
                          Edit
                        </button>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Empty state */}
          {scholarships.length === 0 && <div className="text-center py-12" data-id="element-2506">
              <div className="mx-auto h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-2507">
                <BriefcaseIcon className="h-6 w-6 text-gray-400" data-id="element-2508" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2509">
                No scholarships yet
              </h3>
              <p className="text-gray-500 mb-4" data-id="element-2510">
                Get started by creating your first scholarship program
              </p>
              <button onClick={() => navigate('/employer/create-scholarship')} className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors" data-id="element-2511">
                Create Scholarship
              </button>
            </div>}
        </div>
      </div>
    </Layout>;
};
export default Scholarships;